import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Typography, type TypographyProps } from '@heroui/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ImportHistoryAlert } from './ImportHistoryAlert';
import {
    IMPORT_HISTORY_COLUMN_LABEL_KEY,
    importHistoryColumnsOf,
    isNumericColumn,
} from './importHistoryColumns';
import { ImportHistoryEmpty } from './ImportHistoryEmpty';
import { ImportHistoryPagination } from './ImportHistoryPagination';
import { ImportHistoryRow } from './ImportHistoryRow';
import { ImportHistorySkeletonRow } from './ImportHistorySkeletonRow';
import type { ImportHistoryState } from './useImportHistory';

type ImportHistoryTableProps = {
    history: ImportHistoryState;
    /** A phone: two columns, the file with everything else under it, and the value. */
    isNarrow: boolean;
};

const SKELETON_ROWS = [0, 1, 2];

const TITLE_ID = 'branch-import-history-title';

// The section's title sits under Upload's own heading.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => (
    <h2 {...domProps} id={TITLE_ID}>
        {children}
    </h2>
);

/**
 * The branch's **Import history** as a HeroUI `Table`, newest first: date,
 * file, rows, products created, units, the stock's net value, who imported
 * it and its status. No undo: after onboarding the server refuses it.
 *
 * The title and the column headers show at once; while the first page loads
 * the body holds placeholder rows. A failed first read replaces the table
 * with a notice and Try again; a failed later read leaves the table as it
 * was under a notice of its own. With no imports the body says so. Past one
 * page, the footer says which imports are showing, with Previous and Next.
 */
export const ImportHistoryTable: React.FC<ImportHistoryTableProps> = ({
    history,
    isNarrow,
}) => {
    const { t } = useTranslation('pages');
    const columns = importHistoryColumnsOf(isNarrow);
    const isBusy =
        history.status === 'loading' ||
        ((history.status === 'ready' || history.status === 'empty') &&
            history.refresh === 'pending');

    return (
        <section
            aria-busy={isBusy}
            aria-labelledby={TITLE_ID}
            className="flex flex-col gap-3"
            {...getNodeTestIdProp('branch-import-history')}
        >
            <div className="flex flex-col gap-1">
                <Typography render={asH2} type="h4">
                    {t('catalogImport.branchImport.history.title')}
                </Typography>
                <Typography color="muted" type="body-sm">
                    {t('catalogImport.branchImport.history.intro')}
                </Typography>
            </div>
            {history.status === 'error' ? (
                <ImportHistoryAlert kind="load" onRetry={history.onRetry} />
            ) : (
                <>
                    {history.status !== 'loading' &&
                    history.refresh === 'error' ? (
                        <ImportHistoryAlert
                            kind="refresh"
                            onRetry={history.onRetry}
                        />
                    ) : null}
                    <Table>
                        <Table.ScrollContainer>
                            <Table.Content
                                aria-label={t(
                                    'catalogImport.branchImport.history.title',
                                )}
                                className="text-sm"
                            >
                                <Table.Header>
                                    {columns.map((column) => (
                                        <Table.Column
                                            className={cn(
                                                isNumericColumn(column) &&
                                                    'text-end',
                                            )}
                                            id={column}
                                            isRowHeader={column === 'file'}
                                            key={column}
                                        >
                                            {t(
                                                IMPORT_HISTORY_COLUMN_LABEL_KEY[
                                                    column
                                                ],
                                            )}
                                        </Table.Column>
                                    ))}
                                </Table.Header>
                                <Table.Body
                                    renderEmptyState={() => (
                                        <ImportHistoryEmpty />
                                    )}
                                >
                                    {history.status === 'loading'
                                        ? SKELETON_ROWS.map((index) => (
                                              <ImportHistorySkeletonRow
                                                  columns={columns}
                                                  index={index}
                                                  key={index}
                                              />
                                          ))
                                        : history.status === 'ready'
                                          ? history.items.map((item) => (
                                                <ImportHistoryRow
                                                    columns={columns}
                                                    item={item}
                                                    key={item.id}
                                                />
                                            ))
                                          : []}
                                </Table.Body>
                            </Table.Content>
                        </Table.ScrollContainer>
                        {history.status === 'ready' &&
                        history.pagination !== null ? (
                            <Table.Footer>
                                <ImportHistoryPagination
                                    pagination={history.pagination}
                                />
                            </Table.Footer>
                        ) : null}
                    </Table>
                </>
            )}
        </section>
    );
};
